import { addToast, showDialogConfirm, Table } from '@octano/global-ui';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { removeTemporaryUnblock } from '../api';
import useStudentListColumns from '../hooks/useStudentListColumns';
import { StudentListItem } from '../types';
import { useStudentListLoader } from './StudentListLoader';
import TemporaryUnblockModal from './TemporaryUnblockModal';

const keyPrefix = 'financialPortal.currentAccount.list';
const statusKeyPrefix = 'financialPortal.currentAccount.financialStatus';

export default function StudentListTable() {
  const { t } = useTranslation();
  const { data, loading, query, updateQuery, refresh } = useStudentListLoader();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(
    null,
  );

  const handleEdit = useCallback((row: StudentListItem) => {
    setSelectedStudentId(row.studentId);
    setModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (row: StudentListItem) => {
      showDialogConfirm({
        icon: { name: 'warning', color: 'warning' },
        title: t(`${statusKeyPrefix}.deleteTitle`),
        subtitle: t(`${statusKeyPrefix}.deleteSubtitle`),
        btnConfirm: {
          text: t(`${statusKeyPrefix}.confirm`),
          onConfirm: async () => {
            const result = await removeTemporaryUnblock(row.studentId);
            if (result.error) {
              addToast({
                icon: 'error',
                color: 'danger',
                text: t(`${statusKeyPrefix}.errorDeleting`),
              });
              return;
            }
            addToast({
              icon: 'success',
              color: 'success',
              text: t(`${statusKeyPrefix}.successDeleting`),
            });
            refresh();
          },
        },
        btnClose: { text: t(`${statusKeyPrefix}.cancel`) },
      });
    },
    [t, refresh],
  );

  const columns = useStudentListColumns({
    onEdit: handleEdit,
    onDelete: handleDelete,
  });

  const pagination = useMemo(
    () => ({
      totalItems: data?.total ?? 0,
      itemsPerPage: query.items_per_page,
      totalPages: data?.total_pages ?? 0,
      currentPage: query.page,
      onChangePage: (page: number) => updateQuery({ ...query, page }),
    }),
    [data, query, updateQuery],
  );

  return (
    <>
      <Table<StudentListItem>
        columns={columns}
        data={data?.data ?? []}
        pagination={pagination}
        isLoadingResults={loading}
        noResultsText={
          <div className="text-center py-4">
            <p className="fs-18 fw-600 mb-1">
              {t(`${keyPrefix}.emptyTable.title`)}
            </p>
            <p className="text-muted mb-0">
              {t(`${keyPrefix}.emptyTable.body`)}
            </p>
          </div>
        }
      />
      {selectedStudentId !== null && (
        <TemporaryUnblockModal
          isOpen={modalOpen}
          studentId={selectedStudentId}
          onClose={() => {
            setModalOpen(false);
            setSelectedStudentId(null);
          }}
          onSuccess={() => refresh()}
        />
      )}
    </>
  );
}
